import "./GanttChart.css";

import { MouseEvent, useCallback, useState } from "react";
import { JSX } from "react/jsx-runtime";

import { EditFileBlueIcon } from "../../../assets/icons";
import { Button } from "../../forms/Button";
import { GanttEmptyState } from "./components/GanttEmptyState";
import { GanttFilters } from "./components/GanttFilters";
import { GanttLegend } from "./components/GanttLegend";
import { GanttRoadmapRow } from "./components/GanttRoadmapRow";
import { GanttTaskGroup } from "./components/GanttTaskGroup";
import { GanttTaskRow } from "./components/GanttTaskRow";
import { GanttTimeline } from "./components/GanttTimeline";
import { GanttTooltip } from "./components/GanttTooltip";
import { GanttZoomControls } from "./components/GanttZoomControls";
import {
  GanttChartProps,
  GanttChartRoadmapProps,
  GanttChartTasksProps,
  GanttStageLegendItem,
  GanttTask,
  GanttTooltipData,
  TooltipState,
} from "./models";
import { useGanttChart } from "./useGanttChart";
import { getStageColor, sortPhasesByStart } from "./utils";

/** One rendered agenda row: the task plus the parent object that labels it. */
interface AgendaRow {
  task: GanttTask;
  objectId: string;
  objectName: string;
  objectType?: string;
}

/**
 * Flexible Gantt chart supporting two variants:
 *  - "project-roadmap" — phase bars + milestone diamonds, quarterly/yearly zoom
 *  - "tasks"           — grouped tasks with deadline bars/diamonds, weekly/monthly zoom
 */
export const GanttChart = (props: GanttChartProps): JSX.Element => {
  const {
    zoom,
    setZoom,
    viewMode,
    setViewMode,
    expandedGroups,
    toggleGroup,
    activeFilters,
    onFilterChange,
    viewStart,
    timelineWidth,
    labelWidth,
    pixelsPerDay,
    todayX,
    headerTicks,
    bodyRef,
    filteredGroups,
    isLoading,
  } = useGanttChart(props);

  const { variant, className, theme = "seed" } = props;
  const tasksProps = variant === "tasks" ? (props as GanttChartTasksProps) : null;
  const roadmapProps = variant === "project-roadmap" ? (props as GanttChartRoadmapProps) : null;
  const roadmapMode = roadmapProps?.mode ?? "phase";
  const emptyObjectNoun = roadmapMode === "stage" ? "deals" : "projects";

  /*
    The roadmap sits flush on its page — that page supplies the heading and the white surface, so the
    chart drops its own card chrome (see .GanttChart_roadmap) and its title, which would otherwise read
    as a second one. Tasks keeps the card: it sits on a grey dashboard surface where it reads as a panel.
  */
  const isRoadmap = variant === "project-roadmap";
  const rootClassName = `GanttChart GanttChart_${theme}${isRoadmap ? " GanttChart_roadmap" : ""}${
    className ? ` ${className}` : ""
  }`;

  // Onboarding empty state (no data at all). For roadmaps this matches the deals
  // approach (illustration + copy) with an optional "add" CTA; for tasks it guides
  // the user to apply a workflow / create tasks.
  const emptyState =
    variant === "project-roadmap"
      ? {
          title: "Your roadmap is almost ready",
          subtext: `Add ${emptyObjectNoun} to populate your roadmap.`,
          action: roadmapProps?.onAddObjectsClick
            ? { label: `Add ${emptyObjectNoun} to the roadmap`, onClick: roadmapProps.onAddObjectsClick }
            : undefined,
        }
      : {
          title: "No tasks to show yet",
          subtext: "Apply a workflow to a project, or create tasks on a project, to see them on this screen.",
          action: undefined,
        };

  // While a filter/search is active, force every group open so matches are visible without clicking.
  const anyTaskFilterActive =
    variant === "tasks" &&
    !!(activeFilters.object || activeFilters.assignee?.length || activeFilters.status?.length || activeFilters.search);

  /*
    Agenda: a flat, date-sorted list across every project/deal, from whichever source the caller has.
    `agendaTasks` is one paged query the host owns — the only way a lazily loading caller can show the
    agenda without fetching every group's rows. Otherwise it is flattened out of `filteredGroups`,
    already filtered (assignee/object/status/search) by the chart. Undated tasks sort to the end either
    way, since a page of rows says nothing about where the server put its nulls.
  */
  const isAgendaView = variant === "tasks" && viewMode === "agenda";
  const hostAgendaTasks = tasksProps?.agendaTasks;
  const agendaRows: AgendaRow[] = isAgendaView
    ? (hostAgendaTasks
        ? hostAgendaTasks.map(({ objectId, objectName, objectType, ...task }) => ({
            task,
            objectId,
            objectName,
            objectType,
          }))
        : filteredGroups.flatMap((group) =>
            group.tasks.map((task) => ({
              task,
              objectId: group.id,
              objectName: group.name,
              objectType: group.objectType,
            }))
          )
      ).sort((a, b) => (a.task.deadlineDate?.getTime() ?? Infinity) - (b.task.deadlineDate?.getTime() ?? Infinity))
    : [];

  const isAgendaLoading =
    isAgendaView &&
    (Boolean(tasksProps?.isLoadingAgendaTasks) || (!hostAgendaTasks && filteredGroups.some((g) => g.isLoadingTasks)));

  /*
    The onboarding empty state means "there is nothing here at all", so it has to be judged against
    whatever the current presentation actually renders. In agenda that is `agendaRows`, which a host
    fetches on its own request: judging it by the group headers alone hid a perfectly good agenda
    behind "No tasks to show yet" whenever the two requests disagreed — the header request failing, or
    returning nothing, while the agenda's succeeded. It also took the view switcher down with it, so
    there was no way back to the timeline.

    Both still have to be empty for the timeline, since with no host agenda the rows are flattened out
    of the groups; and rows present with no groups is only reachable when the two requests disagree.
  */
  const isRoadmapEmpty = variant === "project-roadmap" && (roadmapProps?.rows.length ?? 0) === 0;
  const hasNoGroups = (tasksProps?.groups.length ?? 0) === 0;
  const isTasksEmpty = variant === "tasks" && hasNoGroups && (!isAgendaView || agendaRows.length === 0);
  const isEmpty = isRoadmapEmpty || isTasksEmpty;

  const showToday = todayX >= 0 && todayX <= timelineWidth;

  // ── Hover tooltip ───────────────────────────────────────────────────────────
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const showTooltip = useCallback((data: GanttTooltipData, e: MouseEvent): void => {
    setTooltip({ data, x: e.clientX, y: e.clientY });
  }, []);
  const hideTooltip = useCallback((): void => setTooltip(null), []);

  // ── Stage legend (stage mode only) ────────────────────────────────────────
  // Build a positional legend from the row with the most stages (the canonical pipeline).
  let stageLegend: GanttStageLegendItem[] = [];
  if (roadmapMode === "stage" && roadmapProps) {
    const canonical = roadmapProps.rows.reduce<GanttChartRoadmapProps["rows"][number]["phases"]>(
      (best, r) => (r.phases.length > best.length ? r.phases : best),
      []
    );
    // The same sort the rows pack their bars with, so swatch i is the colour of the bar at position i.
    const ordered = sortPhasesByStart(canonical);
    stageLegend = ordered.map((p, i) => {
      const { bg, border } = getStageColor(i, ordered.length);
      return { label: p.label, fill: bg, border };
    });
  }

  if (isLoading) {
    // Skeleton (not a spinner) so it matches the Kanban/List loading treatment and there's no
    // layout jump when the real grid replaces it: a header row + a few placeholder task rows,
    // each a label-column block and an offset timeline bar.
    return (
      <div className={rootClassName} aria-busy="true">
        <div className="GanttTopBar">
          {/* No title placeholder on the roadmap — its controls sit left, so a leading block would jump. */}
          {!isRoadmap && <span className="GanttSkeleton GanttSkeleton_title" />}
          <span className="GanttSkeleton GanttSkeleton_controls" />
        </div>
        <div className="GanttSkeletonBody">
          <div className="GanttSkeletonRow GanttSkeletonRow_header">
            <span className="GanttSkeleton GanttSkeleton_rowLabel" />
            <span className="GanttSkeletonTrack">
              <span className="GanttSkeleton GanttSkeleton_headerTimeline" />
            </span>
          </div>
          {Array.from({ length: 6 }).map((_, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div className="GanttSkeletonRow" key={i}>
              <span className="GanttSkeleton GanttSkeleton_rowLabel" />
              <span className="GanttSkeletonTrack">
                <span
                  className="GanttSkeleton GanttSkeleton_bar"
                  style={{ width: `${34 + ((i * 17) % 42)}%`, marginLeft: `${(i * 23) % 34}%` }}
                />
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={rootClassName}>
      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      {variant === "tasks" && tasksProps?.filterOptions && !isTasksEmpty && (
        <GanttFilters options={tasksProps.filterOptions} active={activeFilters} onChange={onFilterChange} />
      )}

      <div className="GanttTopBar">
        {variant === "tasks" && <span className="body1 GanttChartTitle">Gantt Chart</span>}
        {!isEmpty && (
          <div className="GanttTopBarRight">
            {variant === "tasks" && (
              <div className="GanttSegmented" role="group" aria-label="View mode">
                <button
                  type="button"
                  className={`GanttSegmentedOption body3${viewMode === "timeline" ? " GanttSegmentedOptionActive" : ""}`}
                  onClick={() => setViewMode("timeline")}
                  aria-pressed={viewMode === "timeline"}
                >
                  Timeline
                </button>
                <button
                  type="button"
                  className={`GanttSegmentedOption body3${viewMode === "agenda" ? " GanttSegmentedOptionActive" : ""}`}
                  onClick={() => setViewMode("agenda")}
                  aria-pressed={viewMode === "agenda"}
                >
                  Agenda
                </button>
              </div>
            )}
            <GanttZoomControls variant={variant} zoom={zoom} onChange={setZoom} />
            {variant === "tasks" && tasksProps?.onTableOptions && (
              <Button
                text="Table options"
                variant="quaternary"
                size="extraSmall"
                iconPosition="right"
                onClick={tasksProps.onTableOptions}
                icon={
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                    <circle cx="7" cy="3" r="1.2" fill="currentColor" />
                    <circle cx="7" cy="7" r="1.2" fill="currentColor" />
                    <circle cx="7" cy="11" r="1.2" fill="currentColor" />
                  </svg>
                }
              />
            )}
            {variant === "project-roadmap" && roadmapProps?.onEditProjectsClick && (
              <Button
                className="GanttTopBarEditButton"
                text={roadmapMode === "stage" ? "Edit roadmap" : "Edit projects"}
                variant="secondary"
                size="small"
                corners="rounded"
                iconPosition="right"
                // Hugs its label, so the click target doesn't reach 160px back across the top bar.
                minWidth={false}
                onClick={roadmapProps.onEditProjectsClick}
                icon={<EditFileBlueIcon />}
              />
            )}
          </div>
        )}
      </div>

      {/* ── Scrollable body ─────────────────────────────────────────────── */}
      <div className="GanttBody" ref={bodyRef}>
        {isEmpty ? (
          <GanttEmptyState title={emptyState.title} subtext={emptyState.subtext} action={emptyState.action} />
        ) : (
          /* Total content width = label + timeline */
          <div className="GanttContent" style={{ width: labelWidth + timelineWidth }}>
            {/* ── Header row (sticky top) ─────────────────────────────────── */}
            <div className="GanttHeaderRow">
              {/* Corner cell (sticky top + sticky left) */}
              <div className="GanttHeaderCorner GanttHeaderLabel" style={{ width: labelWidth }}>
                {variant === "tasks" ? (
                  <span className="body4 GanttHeaderColumnLabel">Project / Task</span>
                ) : (
                  <span className="body4 GanttHeaderColumnLabel">{roadmapMode === "stage" ? "Deal" : "Project"}</span>
                )}
              </div>
              <GanttTimeline ticks={headerTicks} timelineWidth={timelineWidth} todayX={todayX} showToday={showToday} />
            </div>

            {/* ── Data rows ───────────────────────────────────────────────── */}
            {variant === "project-roadmap" &&
              (roadmapProps?.rows ?? []).map((row) => (
                <GanttRoadmapRow
                  key={row.id}
                  row={row}
                  mode={roadmapMode}
                  viewStart={viewStart}
                  pixelsPerDay={pixelsPerDay}
                  timelineWidth={timelineWidth}
                  todayX={todayX}
                  labelWidth={labelWidth}
                  onClick={roadmapProps?.onRowClick}
                  onPhaseClick={roadmapProps?.onPhaseClick}
                  onMilestoneClick={roadmapProps?.onMilestoneClick}
                  onKeyDateClick={roadmapProps?.onKeyDateClick}
                  onShowTooltip={showTooltip}
                  onHideTooltip={hideTooltip}
                />
              ))}

            {variant === "tasks" &&
              !isAgendaView &&
              filteredGroups.map((group) => (
                <GanttTaskGroup
                  key={group.id}
                  group={group}
                  isExpanded={anyTaskFilterActive || expandedGroups.has(group.id)}
                  viewStart={viewStart}
                  pixelsPerDay={pixelsPerDay}
                  timelineWidth={timelineWidth}
                  todayX={todayX}
                  labelWidth={labelWidth}
                  initialTaskLimit={tasksProps?.initialTaskLimit}
                  onToggle={toggleGroup}
                  onTaskClick={tasksProps?.onTaskClick}
                  onObjectClick={tasksProps?.onObjectClick}
                  onShowMore={tasksProps?.onGroupShowMore}
                  onShowTooltip={showTooltip}
                  onHideTooltip={hideTooltip}
                />
              ))}

            {isAgendaView &&
              agendaRows.map(({ task, objectId, objectName, objectType }) => (
                <GanttTaskRow
                  key={`${objectId}-${task.id}`}
                  task={task}
                  groupId={objectId}
                  viewStart={viewStart}
                  pixelsPerDay={pixelsPerDay}
                  timelineWidth={timelineWidth}
                  todayX={todayX}
                  labelWidth={labelWidth}
                  objectMeta={{ label: objectName, type: objectType ?? "" }}
                  onClick={tasksProps?.onTaskClick}
                  onShowTooltip={showTooltip}
                  onHideTooltip={hideTooltip}
                />
              ))}

            {/* The agenda is one paged list rather than a page per group, so it pages on its own. */}
            {isAgendaView && tasksProps?.hasMoreAgendaTasks && (
              <div className="GanttGroupsShowMore">
                <Button
                  text={tasksProps.isLoadingAgendaTasks ? "Loading…" : "Show more tasks"}
                  variant="secondary"
                  size="extraSmall"
                  minWidth={false}
                  disabled={tasksProps.isLoadingAgendaTasks}
                  onClick={() => tasksProps.onAgendaShowMore?.()}
                />
              </div>
            )}

            {/* The groups themselves are paged server-side, so without this the objects after the
                first page would never be reachable. Timeline only — the agenda pages its own rows and
                is not a list of objects. */}
            {variant === "tasks" && !isAgendaView && tasksProps?.hasMoreGroups && (
              <div className="GanttGroupsShowMore">
                <Button
                  text={tasksProps.isLoadingMoreGroups ? "Loading…" : "Show more objects"}
                  variant="secondary"
                  size="extraSmall"
                  minWidth={false}
                  disabled={tasksProps.isLoadingMoreGroups}
                  onClick={() => tasksProps.onShowMoreGroups?.()}
                />
              </div>
            )}

            {variant === "tasks" && (isAgendaView ? agendaRows.length === 0 : filteredGroups.length === 0) && (
              <div className="GanttEmpty">
                {/* The agenda's rows are their own request, so it opens with a fetch in flight —
                    "no matches" would be wrong until that lands. */}
                <p className="body2">{isAgendaLoading ? "Loading tasks…" : "No tasks match the current filters."}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Legend ──────────────────────────────────────────────────────── */}
      {!isEmpty &&
        (variant === "project-roadmap" ? (
          <GanttLegend variant="project-roadmap" mode={roadmapMode} stages={stageLegend} />
        ) : (
          <GanttLegend variant="tasks" />
        ))}

      {/* ── Hover tooltip ───────────────────────────────────────────────── */}
      {tooltip && <GanttTooltip data={tooltip.data} x={tooltip.x} y={tooltip.y} />}
    </div>
  );
};
